import React, { FC, useEffect, useState } from 'react'
import { Alert, Button, Collapse, type CollapseProps, Form, Input, Popconfirm } from 'antd'
import { createSelector } from '@reduxjs/toolkit'
import { useTranslation } from 'react-i18next'

import { CustomModal } from '../../common/CustomModal'
import './index.less'
import { useAppDispatch, useAppSelector } from '../../../core/hooks'
import { CardinalApiState, skipRecoveryKeyCollectionStep, provideRecoveryKeys } from '../../../core/services/auth.api'
import { RecoveryKeyOutcomeEnum } from '../../../helpers/types'

const MAX_WAIT_FOR_SKIP = 5

const reduxSelector = createSelector(
  (state: { cardinalApi: CardinalApiState }) => state.cardinalApi,
  (cardinalApi: CardinalApiState) => ({
    recoveryKeyRequest: cardinalApi.recoveryKeyRequest,
    triedRecoveryKeys: cardinalApi.triedRecoveryKeys,
  }),
)

interface RecoveryKeysFormType {
  recoveryKey: string
}

export const ModalRecoveryKeyRequest: FC = () => {
  const { t } = useTranslation()
  const [form] = Form.useForm()

  const dispatch = useAppDispatch()
  const { recoveryKeyRequest, triedRecoveryKeys } = useAppSelector(reduxSelector)
  const isRecoveryKeyRequested = !!recoveryKeyRequest
  const secondsToWaitBeforeAllowingSkip = MAX_WAIT_FOR_SKIP
  const [seconds, setSeconds] = useState(0)
  const [previousTriedRecoveryKeys, setPreviousTriedRecoveryKeys] = useState<{ [key: string]: RecoveryKeyOutcomeEnum }>({})
  const maxKeys = recoveryKeyRequest?.reasons.length ?? 1
  const missingKeysByOwner = recoveryKeyRequest?.missingKeysByOwner ?? []

  useEffect(() => {
    let i = 0 // local counter avoids any stale state issues
    const id = window.setInterval(() => {
      i += 1
      setSeconds(i)
      if (i >= MAX_WAIT_FOR_SKIP) window.clearInterval(id)
    }, 1000)

    return () => window.clearInterval(id)
  }, [isRecoveryKeyRequested])

  const handleSubmit = (values: RecoveryKeysFormType) => {
    setPreviousTriedRecoveryKeys(triedRecoveryKeys ?? {})
    dispatch(provideRecoveryKeys({ recoveryKeys: [values.recoveryKey] }))
  }
  const getCustomFooter = () => {
    return [
      <Popconfirm
        key="confirmClose"
        title={t('user.recoveryKeyRequest.popconfirm.title')}
        description={
          <div>
            <p>{t('user.recoveryKeyRequest.popconfirm.description1')}</p>
            <p>{t('user.recoveryKeyRequest.popconfirm.description2')}</p>
          </div>
        }
        onConfirm={() => dispatch(skipRecoveryKeyCollectionStep())}
        okText={t('user.recoveryKeyRequest.popconfirm.okText')}
        okButtonProps={{ danger: true }}
        cancelText={t('user.recoveryKeyRequest.popconfirm.cancelText')}
        placement="topRight"
      >
        <Button loading={seconds < secondsToWaitBeforeAllowingSkip} key="back">
          {seconds < secondsToWaitBeforeAllowingSkip
            ? t('user.recoveryKeyRequest.skip_in_seconds', { seconds: secondsToWaitBeforeAllowingSkip - seconds })
            : t('user.recoveryKeyRequest.skip')}
        </Button>
      </Popconfirm>,

      <Button key="submit" disabled={maxKeys > 0} type="primary" onClick={() => form.submit()}>
        {t('user.recoveryKeyRequest.submit')}
      </Button>,
    ]
  }
  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: t('user.recoveryKeyRequest.collapse.item1.label'),
      children: <p>{t('user.recoveryKeyRequest.collapse.item1.text')}</p>,
    },
    {
      key: '2',
      label: t('user.recoveryKeyRequest.collapse.item2.label'),
      children: <p>{t('user.recoveryKeyRequest.collapse.item2.text')}</p>,
    },
    {
      key: '3',
      label: t('user.recoveryKeyRequest.collapse.item3.label'),
      children: (
        <p>
          {t('user.recoveryKeyRequest.collapse.item3.text.header')}
          <br />
          <strong>{t('user.recoveryKeyRequest.collapse.item3.text.content.line1.title')}</strong> – {t('user.recoveryKeyRequest.collapse.item3.text.content.line1.text')}
          <br />
          <strong>{t('user.recoveryKeyRequest.collapse.item3.text.content.line2.title')}</strong> – {t('user.recoveryKeyRequest.collapse.item3.text.content.line2.text')}
          <br />
          <strong>{t('user.recoveryKeyRequest.collapse.item3.text.content.line3.title')}</strong> – {t('user.recoveryKeyRequest.collapse.item3.text.content.line3.text')}
        </p>
      ),
    },
  ]

  const invalidRecoveryKeyAttempts = Object.entries(triedRecoveryKeys || {}).filter(([, outcome]) => {
    return outcome === RecoveryKeyOutcomeEnum.INVALID_KEY
  })

  const successfulRecoveryKeyAttempts = Object.entries(triedRecoveryKeys || {}).filter(([, outcome]) => {
    return outcome === RecoveryKeyOutcomeEnum.RECOVERY
  })

  const noRecoveryKeyAttempts = Object.entries(triedRecoveryKeys || {}).filter(([, outcome]) => {
    return outcome === RecoveryKeyOutcomeEnum.NO_RECOVERY
  })

  const alreadyUsedRecoveryKeyAttempts = Object.entries(triedRecoveryKeys || {}).filter(([, outcome]) => {
    return outcome === RecoveryKeyOutcomeEnum.RECOVERY_ALREADY_DONE
  })

  const latestKeyOutcome = Object.entries(triedRecoveryKeys || {}).find(([key]) => !(key in previousTriedRecoveryKeys))?.[1]

  return (
    <CustomModal isVisible={isRecoveryKeyRequested} title={t('user.recoveryKeyRequest.title')} customFooter={getCustomFooter()} closable={false} primaryBtnDisabled={maxKeys > 0}>
      <div className="modalRecoveryKeyRequest">
        <Alert
          type="warning"
          description={
            <div className="modalRecoveryKeyRequest__content">
              <h6>{t('user.recoveryKeyRequest.alert.title', { maxKeys })}</h6>
              <div>
                <p>{t('user.recoveryKeyRequest.alert.missingKeys', { maxKeys })}</p>
                {missingKeysByOwner.length > 0 && (
                  <>
                    <p>{t('user.recoveryKeyRequest.alert.byOwner')}</p>
                    <ul>
                      {missingKeysByOwner.map(({ dataOwnerId, name, isSelf, missingKeys }) => (
                        <li key={dataOwnerId}>
                          {t(isSelf ? 'user.recoveryKeyRequest.alert.ownerSelf' : 'user.recoveryKeyRequest.alert.ownerParent', { name: name ?? dataOwnerId, count: missingKeys })}
                        </li>
                      ))}
                    </ul>
                    {missingKeysByOwner.some(({ isSelf }) => !isSelf) && <p>{t('user.recoveryKeyRequest.alert.parentHint')}</p>}
                  </>
                )}
                <p>{t('user.recoveryKeyRequest.alert.instructions')}</p>
              </div>
            </div>
          }
        />
        {invalidRecoveryKeyAttempts.length > 0 && (
          <Alert
            type="error"
            message={t('user.recoveryKeyRequest.invalidKeys')}
            description={invalidRecoveryKeyAttempts.map(([k], idx) => (
              <div key={idx}>{k}</div>
            ))}
          />
        )}
        {noRecoveryKeyAttempts.length > 0 && (
          <Alert
            type="warning"
            message={t('user.recoveryKeyRequest.noRecoveryKeys')}
            description={noRecoveryKeyAttempts.map(([k], idx) => (
              <div key={idx}>{k}</div>
            ))}
          />
        )}
        {alreadyUsedRecoveryKeyAttempts.length > 0 && (
          <Alert
            type="warning"
            message={t('user.recoveryKeyRequest.alreadyRecovered')}
            description={alreadyUsedRecoveryKeyAttempts.map(([k], idx) => (
              <div key={idx}>{k}</div>
            ))}
          />
        )}
        {successfulRecoveryKeyAttempts.length > 0 && (
          <Alert
            type="success"
            message={t('user.recoveryKeyRequest.successfullyRecovered')}
            description={successfulRecoveryKeyAttempts.map(([k], idx) => (
              <div key={idx}>{k}</div>
            ))}
          />
        )}
        <Form
          className="modalRecoveryKeyRequest__form"
          layout="vertical"
          colon={false}
          form={form}
          onFinish={(values) => {
            handleSubmit(values)
          }}
          initialValues={{
            recoveryKey: '',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <Form.Item
              name={'recoveryKey'}
              label={t('user.recoveryKeyRequest.form.recoveryKeyLabel')}
              rules={[{ required: true, message: t('user.recoveryKeyRequest.form.recoveryKeyRequired') }]}
              style={{ width: '100%' }}
              validateStatus={latestKeyOutcome === RecoveryKeyOutcomeEnum.INVALID_KEY ? 'error' : latestKeyOutcome === RecoveryKeyOutcomeEnum.RECOVERY ? 'success' : 'warning'}
              help={
                latestKeyOutcome === RecoveryKeyOutcomeEnum.INVALID_KEY
                  ? t('user.recoveryKeyRequest.form.recoveryKeyOutcome.invalid')
                  : latestKeyOutcome === RecoveryKeyOutcomeEnum.RECOVERY
                    ? t('user.recoveryKeyRequest.form.recoveryKeyOutcome.success')
                    : latestKeyOutcome === RecoveryKeyOutcomeEnum.NO_RECOVERY
                      ? t('user.recoveryKeyRequest.form.recoveryKeyOutcome.no_recovery')
                      : latestKeyOutcome === RecoveryKeyOutcomeEnum.RECOVERY_ALREADY_DONE
                        ? t('user.recoveryKeyRequest.form.recoveryKeyOutcome.already_done')
                        : ''
              }
            >
              <Input.Search
                placeholder={t('user.recoveryKeyRequest.form.recoveryKeyLabel')}
                size="large"
                enterButton="Validate"
                onSearch={() => {
                  form.submit()
                }}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </div>
        </Form>
        <Collapse size="small" items={items} />
      </div>
    </CustomModal>
  )
}
